import type { HydraCollection } from "@verbleif/lib/src/composables/Api/Type/hydra.interface";
import type { AxiosRequestConfig } from "axios";
import { api } from "./api";

export enum BaseUrl {
  LOGIN = "https://login.verbleif.dev",
  API = "https://api.verbleif.dev",
}

export async function getAll<T = any>({
  url,
  initialParams,
  perPage = 100,
  signal,
  _retry = true,
  concurrency = 1,
  onPage,
}: {
  url: string
  initialParams?: URLSearchParams
  perPage?: number
  signal?: AbortSignal
  _retry?: boolean
  concurrency?: number
  onPage?: (items: T[]) => void
}): Promise<T[]> {
  async function fetchPage(page: number): Promise<HydraCollection<T>> {
    const params = new URLSearchParams(initialParams);
    params.set("perPage", perPage.toString());
    params.set("page", page.toString());
    const { data } = await api.get<HydraCollection<T>>(url, { params, signal, _retry } as AxiosRequestConfig & { _retry?: boolean });
    return data;
  }

  const first = await fetchPage(1);
  let items = [...(first.member ?? [])];
  onPage?.(items.slice());
  const totalItems = first.totalItems;

  function hasAllItems(): boolean {
    return totalItems != null && items.length >= totalItems;
  }

  // Endpoints backed by a plain array provider ignore page/perPage and return every item on every page.
  // Paging on would refetch the same list forever, so a page larger than perPage ends the fetch.
  if (signal?.aborted || items.length < perPage || items.length > perPage || hasAllItems()) {
    return items;
  }

  if (totalItems == null || concurrency <= 1) {
    let page = 2;
    while (!signal?.aborted) {
      const next = await fetchPage(page);
      const member = next.member ?? [];
      items = [...items, ...member];
      onPage?.(items.slice());
      if (member.length < perPage || member.length > perPage || hasAllItems()) {
        break;
      }
      page += 1;
    }
    return items;
  }

  const totalPages = Math.ceil(totalItems / perPage);
  const pageMembers: Array<typeof items | undefined> = [];
  pageMembers[0] = items;
  let nextToAppend = 1;
  let nextPage = 2;
  let failed: unknown = null;

  function emitReady() {
    while (pageMembers[nextToAppend]) {
      items = [...items, ...pageMembers[nextToAppend]!];
      onPage?.(items.slice());
      nextToAppend += 1;
    }
  }

  async function worker() {
    while (!failed && !signal?.aborted) {
      const page = nextPage;
      nextPage += 1;
      if (page > totalPages) {
        return;
      }
      try {
        const data = await fetchPage(page);
        if (failed || signal?.aborted) {
          return;
        }
        pageMembers[page - 1] = data.member ?? [];
        emitReady();
      } catch (error) {
        if (signal?.aborted) {
          return;
        }
        failed = error;
        throw error;
      }
    }
  }

  const workers = Math.min(concurrency, Math.max(totalPages - 1, 0));
  await Promise.all(Array.from({ length: workers }, () => worker()));
  if (failed) {
    throw failed;
  }
  return items;
}
